// Admin → Audit log. Every privileged action (auth, billing, moderation, admin
// user ops, impersonation). docs/replan-v2.md (admin console, slice 3).
// English chrome. Migrated to the console component system in M7c / P6.
//
// This surface got more important in M7c: every refund and every comp grant
// now writes a row here carrying the amount, currency and reason. The metadata
// is therefore rendered as a readable key/value list rather than a raw
// JSON.stringify blob squeezed into the Target column.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import {
  DataTable,
  DetailRow,
  DetailSection,
  Drawer,
  FilterBar,
  PageHeader,
  StatusBadge,
  actionsColumn,
  useDebounced,
  useTableState,
  type Column,
} from '~/components';
import { listAudit, type AuditEntry } from '~/lib/admin';

export const Route = createFileRoute('/_authed/audit')({
  component: AuditPage,
});

// Common action prefixes as quick filters.
const PREFIX_OPTIONS = [
  { value: 'admin.', label: 'Admin' },
  { value: 'billing.', label: 'Billing' },
  { value: 'auth.', label: 'Auth' },
  { value: 'messaging.', label: 'Messaging' },
  { value: 'task.', label: 'Tasks' },
  { value: 'kids.', label: 'Kids' },
];

/** Impersonation is the single most sensitive action; it must never blend in. */
function actionTone(action: string): 'warning' | 'info' | 'neutral' {
  if (action.startsWith('admin.impersonate')) return 'warning';
  if (action.startsWith('admin.') || action.startsWith('billing.')) return 'info';
  return 'neutral';
}

function AuditPage() {
  const table = useTableState({ filterKeys: ['prefix'] });
  const [openEntry, setOpenEntry] = useState<AuditEntry | null>(null);

  // Free-text and the prefix chips both narrow the same server-side `action`
  // filter; the chips are just common prefixes.
  const typed = useDebounced(table.q.trim(), 300);
  const action = typed || table.filters.prefix || '';

  const audit = useQuery({
    queryKey: ['audit', action],
    queryFn: ({ signal }) => listAudit({ action: action || undefined }, signal),
  });

  const columns: Column<AuditEntry>[] = [
    {
      key: 'createdAt',
      header: 'When',
      alwaysVisible: true,
      render: (e) => (
        <span className="whitespace-nowrap text-xs text-admin-500">
          {formatDateTime(e.createdAt)}
        </span>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      render: (e) => <StatusBadge status={e.action} label={e.action} tone={actionTone(e.action)} />,
    },
    {
      key: 'actorLabel',
      header: 'Actor',
      render: (e) =>
        e.actorLabel ? (
          <span className="text-admin-700">{e.actorLabel}</span>
        ) : (
          <span className="text-admin-300">system</span>
        ),
    },
    {
      key: 'target',
      header: 'Target',
      hideBelow: 'md',
      render: (e) =>
        e.targetEntityType ? (
          <span className="text-admin-600">
            {e.targetEntityType}
            {e.targetEntityId && (
              <code className="ms-1.5 text-xs text-admin-400">{e.targetEntityId.slice(0, 8)}</code>
            )}
          </span>
        ) : (
          <span className="text-admin-300">—</span>
        ),
    },
    {
      key: 'metadata',
      header: 'Details',
      hideBelow: 'lg',
      render: (e) => {
        const keys = Object.keys(e.metadata);
        if (keys.length === 0) return <span className="text-admin-300">—</span>;
        return (
          <span className="truncate text-xs text-admin-500">
            {keys
              .slice(0, 3)
              .map((k) => `${k}: ${formatValue(e.metadata[k])}`)
              .join(' · ')}
            {keys.length > 3 && ` +${keys.length - 3}`}
          </span>
        );
      },
    },
    // The audit log is APPEND-ONLY — there is deliberately nothing here that
    // edits or deletes an entry. Inspect and copy only.
    actionsColumn<AuditEntry>({
      label: (e) => `${e.action} entry`,
      primary: (e) => ({
        id: 'open',
        label: 'Open entry',
        icon: 'eye',
        onSelect: () => setOpenEntry(e),
      }),
      actions: (e) => [
        {
          id: 'copy-target',
          label: 'Copy target id',
          icon: 'copy',
          onSelect: () => void navigator.clipboard?.writeText(e.targetEntityId ?? ''),
          disabled: !e.targetEntityId,
          disabledReason: 'This entry has no target record.',
        },
        {
          id: 'filter-action',
          label: 'Filter by this action',
          icon: 'filter',
          onSelect: () => table.setQuery(e.action),
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Audit log"
        description="Every privileged action — who did what, when. Newest first."
      />

      <DataTable
        columns={columns}
        rows={audit.data ?? []}
        rowKey={(e) => e.id}
        loading={audit.isLoading}
        error={audit.error}
        onRetry={() => void audit.refetch()}
        onRowClick={(e) => setOpenEntry(e)}
        isRowActive={(e) => e.id === openEntry?.id}
        columnVisibility
        caption="Audit log"
        empty={{
          icon: 'history',
          title: table.isFiltered ? 'No entries match these filters' : 'No entries yet',
          description: table.isFiltered
            ? 'Try a different action prefix.'
            : 'Privileged actions are recorded here as they happen.',
        }}
        toolbar={
          <FilterBar
            query={table.q}
            onQueryChange={table.setQuery}
            searchPlaceholder="Filter by action (e.g. billing.refund)"
            filters={[
              {
                key: 'prefix',
                label: 'Area',
                value: table.filters.prefix ?? '',
                options: PREFIX_OPTIONS,
              },
            ]}
            onFilterChange={table.setFilter}
            onClear={table.clearFilters}
            isFiltered={table.isFiltered}
          />
        }
      />

      <Drawer
        open={openEntry != null}
        onOpenChange={(open) => !open && setOpenEntry(null)}
        title="Audit entry"
        description={openEntry ? formatDateTime(openEntry.createdAt) : undefined}
        badge={
          openEntry && (
            <StatusBadge
              status={openEntry.action}
              label={openEntry.action}
              tone={actionTone(openEntry.action)}
            />
          )
        }
      >
        {openEntry && (
          <>
            <DetailSection title="Who">
              <DetailRow label="Actor">{openEntry.actorLabel ?? 'system'}</DetailRow>
              {openEntry.actorId && (
                <DetailRow label="Actor id">
                  <code className="break-all text-xs">{openEntry.actorId}</code>
                </DetailRow>
              )}
              <DetailRow label="IP">
                {/* No PII in logs beyond opaque ids (CLAUDE.md §4) — an IP is
                    shown only when the API chose to record one. */}
                {openEntry.ip ?? <span className="text-admin-400">not recorded</span>}
              </DetailRow>
            </DetailSection>

            <DetailSection title="What">
              <DetailRow label="Action">
                <code className="text-xs">{openEntry.action}</code>
              </DetailRow>
              <DetailRow label="Target">
                {openEntry.targetEntityType ?? <span className="text-admin-400">—</span>}
              </DetailRow>
              {openEntry.targetEntityId && (
                <DetailRow label="Target id">
                  <code className="break-all text-xs">{openEntry.targetEntityId}</code>
                </DetailRow>
              )}
            </DetailSection>

            <DetailSection title="Details">
              {Object.keys(openEntry.metadata).length === 0 ? (
                <p className="py-2 text-sm text-admin-400">No extra detail recorded.</p>
              ) : (
                Object.entries(openEntry.metadata).map(([key, value]) => (
                  <DetailRow key={key} label={key}>
                    <span className="break-all">{formatValue(value)}</span>
                  </DetailRow>
                ))
              )}
            </DetailSection>
          </>
        )}
      </Drawer>
    </div>
  );
}

function formatValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
